import React, { PropsWithChildren, useEffect, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "store/reducers";
import { Sidebar } from "./sidebar/Sidebar";
import { PageContent } from "./page-content/PageContent";
import HamburgerIcon from "assets/icons/hamburger.svg?react";
import Loading from "components/loading/Loading";
import styles from "./DashboardLayout.module.scss";
import {
    mustBeLoggedIn,
    useRedirectIfDisallowed
} from "hooks/useRedirectIfDisallowed";
import { useSwitcherSdk } from "hooks/useSwitcherSdk";
import { useRefreshToken } from "hooks/useRefreshToken";
import { SubscriptionStatusBanner } from "components/banners/custom-banners/SubscriptionStatusBanner";
import { useSupportSystem } from "hooks/useSupportSystem";
import { ModalManager } from "components/modal/ModalManager";
import { SkipToContentLink } from "components/SkipToContentLink";
import Logo from "assets/icons/switcher-horizontal.svg?url";
import { matchPath } from "react-router";
import { creationToolsRoutes, videoCMSRoutes } from "router";
import { useClaimCheck } from "hooks/useClaimCheck";
import { AppDispatch } from "store/store";
import { closeCurrentModal } from "store/modal/slice";
import { useHasAccess } from "hooks/useHasAccess";
import { useTokenRefreshInterval } from "hooks/useTokenRefreshInterval";

export const DashboardLayout: React.FC<PropsWithChildren> = () => {
    const { isInBrowser } = useSwitcherSdk(true);
    let isDisallowed = true;
    const { pathname, search } = useLocation();
    const navigate = useNavigate();
    const hasAppClaim = useClaimCheck("app");
    const isUserPaused = useHasAccess((u) =>
        u?.Claims?.Details?.Roles?.includes("PausedV2")
    );
    const dispatch = useDispatch<AppDispatch>();

    isDisallowed = useRedirectIfDisallowed(
        mustBeLoggedIn,
        "/login?redirect=" + encodeURIComponent(pathname + search)
    );
    isDisallowed = !useRefreshToken().isAuthenticated;

    useSupportSystem(isInBrowser && !isDisallowed);

    const isUserReady =
        useSelector((state: RootState) => state.user)?.userInfo?.User?.Details
            ?.Id != null;

    // Refresh user info and token periodically
    const { startTokenRefreshInterval } = useTokenRefreshInterval();
    // Start the refresh interval when the component mounts
    useEffect(() => {
        // Only start if user is authenticated
        if (isUserReady) {
            startTokenRefreshInterval();
        }
    }, [startTokenRefreshInterval, isUserReady]);

    const [showSidebar, setShowSidebar] = useState<boolean>(false);
    const toggleSidebar = () => {
        setShowSidebar((prev) => !prev);
    };

    const pageSpecificBanner = useSelector(
        (s: RootState) => s.page?.activeBanner
    );

    useEffect(() => {
        const isVideoCMSRoute = creationToolsRoutes.some((route) =>
            matchPath(route.path, pathname)
        );

        const isCreationToolsRoute = videoCMSRoutes.some((route) =>
            matchPath(route.path, pathname)
        );

        const isVideoCMSorCreationRoute =
            isVideoCMSRoute || isCreationToolsRoute;

        // check if user is accessing a pathname within the video CMS or creation tools
        // if they are, make sure they're not paused
        const isPausedInvalidRoute = isUserPaused && isVideoCMSorCreationRoute;

        // check if user is accessing a pathname within the creation tools
        // if they are, make sure they've got the app claim,
        const isNoAppClaimInvalidRoute = !hasAppClaim && isCreationToolsRoute;

        if (isUserReady && (isPausedInvalidRoute || isNoAppClaimInvalidRoute)) {
            // else, redirect them to the home page
            navigate("/home");
        }
    }, [isUserReady, hasAppClaim, pathname, isUserPaused, navigate]);

    useEffect(() => {
        const handleResize = () => {
            //setting the sidebar to false when the window is resized larger than mobile breakpoint
            if (window.innerWidth >= 1024) {
                setShowSidebar(false);
            }
        };

        window.addEventListener("resize", handleResize);

        return () => window.removeEventListener("resize", handleResize);
    }, []);

    useEffect(() => {
        // if route changes, close modals
        // only works on modals that use dispatch
        dispatch(closeCurrentModal());
    }, [dispatch, pathname]);

    return (
        <>
            {isDisallowed ? null : (
                <div className="container-fluid">
                    <Loading isLoading={!isUserReady} offset />

                    {isUserReady && (
                        <div className={styles["main-layout"]}>
                            {isInBrowser && (
                                <>
                                    <SkipToContentLink />
                                    <Sidebar
                                        pathname={pathname}
                                        show={showSidebar}
                                        onToggle={toggleSidebar}
                                    />

                                    <div className={styles["mobile-header"]}>
                                        <HamburgerIcon
                                            onClick={toggleSidebar}
                                        />
                                        <Link to="/home">
                                            <img
                                                className={styles.logo}
                                                src={Logo}
                                                alt="Switcher Logo"
                                            />
                                        </Link>
                                    </div>
                                </>
                            )}
                            <div className="col-sm">
                                {isInBrowser && (pageSpecificBanner ?? <></>)}
                                {isInBrowser && <SubscriptionStatusBanner />}
                                <ModalManager />
                                <div
                                    className={styles["page-content"]}
                                    id="main-content"
                                >
                                    <PageContent showPageHeader={isInBrowser} />
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            )}
        </>
    );
};
